import 'react-phone-input-2/lib/style.css';
import React, {
    type ChangeEvent,
    type FocusEvent,
    type ForwardedRef,
    forwardRef,
    type InputHTMLAttributes,
    type KeyboardEvent,
    type MouseEvent,
    type ReactElement,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react';

import classnames from 'classnames';
import PhoneInput, { type CountryData, type PhoneInputProps } from 'react-phone-input-2';

import { useConfig } from '../../features/config';
import useTranslation from '../../features/locale/useTranslation';
import { useId } from '../../hooks/useId';
import flagsStyles from '../../theme/flags.module.scss';
import { fixControlledValue, omit, resolveOnChangeEvent, triggerFocus } from '../../utils';
import { FieldLabel } from '../FieldLabel';
import { FormControlClearButton } from '../FormControlClearButton';
import { FormControlMessage } from '../FormControlMessage';
import { type InputFocusOptions, type InputRef } from '../Input';

import { LOCALIZED_COUNTRIES } from './localizedCountries';

import styles from './PhoneNumberInput.module.scss';

type PhoneNumberInputChangeEventTarget = BaseProps<PhoneNumberInputChangeEvent>;

export type PhoneNumberInputChangeEvent = {
    target: Omit<PhoneNumberInputChangeEventTarget, 'value'> & { value: string };
    stopPropagation: () => void;
    preventDefault: () => void;
    nativeEvent: ChangeEvent<HTMLInputElement>['nativeEvent'];
};

type BaseProps<T> = {
    id?: string;
    label?: string;
    value?: string | null;
    name?: string;
    error?: boolean | string | null;
    tooltip?: string;
    size?: 'default' | 'small';
    emptyReadOnlyPlaceholder?: string;
    autoFocus?: boolean;

    // react-phone-input-2 props
    preferredCountries?: string[];
    defaultCountry?: string | number;
    searchPlaceholder?: string;
    searchNotFound?: string;
    countryCodeEditable?: boolean;
    allowClear?: boolean;
    showDropdown?: boolean;
    onChange?: (
        event: T,
        extra: {
            value: string;
            data: CountryData;
            formattedValue: string;
        }
    ) => void;
    onFocus?: (event: FocusEvent<HTMLInputElement>, data: CountryData) => void;
    onBlur?: (event: FocusEvent<HTMLInputElement>, data: CountryData) => void;
    onClick?: (event: MouseEvent<HTMLInputElement>, data: CountryData) => void;
    onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
    onEnterKeyPress?: (event: KeyboardEvent<HTMLInputElement>) => void;
    onClear?: () => void;
    isValid?:
        | ((
              value: string,
              country: object,
              countries: object[],
              hiddenAreaCodes: object[]
          ) => boolean | string)
        | boolean;
    onMount?: (value: string, data: CountryData, formattedValue: string) => void;
} & Omit<
    InputHTMLAttributes<HTMLInputElement>,
    | 'type'
    | 'onChange'
    | 'onFocus'
    | 'onBlur'
    | 'onClick'
    | 'onClear'
    | 'onKeyDown'
    | 'onEnterKeyPress'
    | 'value'
    | 'size'
>;

export type PhoneNumberInputProps = BaseProps<PhoneNumberInputChangeEvent>;

export const PhoneNumberInput = forwardRef<InputRef, PhoneNumberInputProps>(
    (props: PhoneNumberInputProps, ref: ForwardedRef<InputRef>): ReactElement => {
        const {
            defaultCountry,
            label,
            name,
            onChange,
            tooltip,
            id,
            error,
            onMount,
            onEnterKeyPress,
            onKeyDown,
            onClick,
            onBlur,
            onFocus,
            onClear,
            isValid,
            searchNotFound,
            searchPlaceholder,
            size = 'default',
            readOnly,
            countryCodeEditable = true,
            disabled,
            showDropdown,
            placeholder = '',
            preferredCountries,
            allowClear: receivedAllowClear,
            emptyReadOnlyPlaceholder,
            autoFocus,
            className,
            value,
            ...rest
        } = props;
        const inputId = useId(id);
        const tooltipId = tooltip ? `${inputId}-tooltip` : '';
        const { t } = useTranslation();
        const inputRef = useRef<HTMLInputElement>(null);
        const [countryData, setCountryData] = useState<Record<string, never> | CountryData>();
        const { defaultAllowClear } = useConfig();
        const allowClear = receivedAllowClear ?? defaultAllowClear;
        const hasPhoneNumber = value && value !== `+${countryData?.dialCode}`;
        // if the country code is not editable, we don't want to show the clear button
        // when the value is the country code only.
        const shouldShowClearButton =
            (allowClear && countryCodeEditable && value) ||
            (allowClear && !countryCodeEditable && hasPhoneNumber);

        const handleChange: PhoneInputProps['onChange'] = (
            _value,
            _countryData,
            _event,
            formattedValue
        ) => {
            setCountryData(_countryData);
            if (onChange) {
                const changedEvent = {
                    stopPropagation: () => _event.stopPropagation(),
                    preventDefault: () => _event.preventDefault(),
                    target: {
                        ...props,
                        value: formattedValue || ''
                    },
                    nativeEvent: _event.nativeEvent
                };
                onChange(changedEvent, {
                    value: _value,
                    data: _countryData as CountryData,
                    formattedValue
                });
            }
        };

        const handleMount: PhoneInputProps['onMount'] = (_value, _countryData) => {
            setCountryData(_countryData);
            onMount?.(_value, _countryData as CountryData, _value);
        };

        const focus = useCallback((option?: InputFocusOptions) => {
            if (inputRef.current) {
                triggerFocus(inputRef.current, option);
            }
        }, []);

        const handleClearClick = useCallback(
            (e: never) => {
                if (inputRef.current) {
                    const newValue = countryCodeEditable ? '' : `+${countryData?.dialCode}`;
                    focus();
                    handleChange(
                        newValue,
                        countryData || {},
                        resolveOnChangeEvent(inputRef.current, e),
                        newValue
                    );
                    onClear?.();
                }
            },
            [countryCodeEditable, countryData?.dialCode, focus]
        );

        useImperativeHandle(ref, () => ({
            focus,
            blur: () => {
                inputRef.current?.blur();
            },
            setSelectionRange: (
                start: number,
                end: number,
                direction?: 'forward' | 'backward' | 'none'
            ) => {
                inputRef.current?.setSelectionRange(start, end, direction);
            },
            select: () => {
                inputRef.current?.select();
            },
            input: inputRef.current
        }));

        const memoizedClearButton = useMemo(
            () => (
                <FormControlClearButton
                    onClick={handleClearClick}
                    aria-label="clear phone number"
                    className={styles.clearButton}
                    // prevent blur event on clear button click
                    onMouseDown={(e: MouseEvent<HTMLElement>) => e.preventDefault()}
                />
            ),
            [handleClearClick]
        );

        useEffect(() => {
            if (autoFocus) {
                focus();
            }
        }, [autoFocus, focus]);

        return (
            <div
                className={classnames(
                    styles.container,
                    {
                        [styles.error]: error,
                        [styles.disabled]: disabled,
                        [styles.readOnly]: readOnly
                    },
                    styles[size]
                )}
            >
                {label && (
                    <FieldLabel
                        htmlFor={inputId}
                        label={label}
                        tooltip={tooltip}
                        tooltipId={tooltipId}
                    />
                )}
                <div className={styles.wrapper}>
                    <PhoneInput
                        autoFormat={false}
                        containerClass={classnames(
                            'react-tel-input',
                            flagsStyles['react-tel-input'],
                            className
                        )}
                        country={readOnly && !value ? undefined : defaultCountry}
                        enableLongNumbers
                        enableTerritories
                        enableSearch
                        inputProps={omit(
                            {
                                ref: inputRef,
                                name,
                                readOnly,
                                disabled,
                                placeholder:
                                    readOnly && !value
                                        ? emptyReadOnlyPlaceholder ?? t('None')
                                        : placeholder,
                                ...rest,
                                id: inputId
                            },
                            ['defaultValue']
                        )}
                        localization={LOCALIZED_COUNTRIES}
                        onChange={handleChange}
                        onMount={handleMount}
                        preferredCountries={preferredCountries}
                        preserveOrder={['preferredCountries']}
                        searchClass={styles.searchInput}
                        value={fixControlledValue(value)}
                        onEnterKeyPress={onEnterKeyPress}
                        onKeyDown={onKeyDown}
                        onClick={onClick}
                        onBlur={onBlur}
                        onFocus={onFocus}
                        isValid={isValid}
                        searchNotFound={searchNotFound}
                        searchPlaceholder={searchPlaceholder}
                        disableSearchIcon
                        disableDropdown={disabled || readOnly}
                        showDropdown={showDropdown}
                        countryCodeEditable={countryCodeEditable}
                    />
                    {allowClear && (
                        <div
                            className={classnames(styles.inputSuffix, {
                                [styles.hidden]: !shouldShowClearButton
                            })}
                        >
                            {memoizedClearButton}
                        </div>
                    )}
                </div>
                {error && typeof error !== 'boolean' && <FormControlMessage message={error} />}
            </div>
        );
    }
);

PhoneNumberInput.displayName = 'PhoneNumberInput';
